"use strict";
/**
 * Loan Application Step Constants
 *
 * 12-step flow matching Sky Lifestyle Web structure:
 * 1. Start (loan setup + disclaimer)
 * 2. KYC (biometric verification)
 * 3. Personal Details
 * 4. Family Member & Friend Contacts
 * 5. Employment Details
 * 6. HR Details (Paymaster)
 * 7. Salary Banking Details
 * 8. Financial Details
 * 9. Required Documents
 * 10. Authorization for Salary Deduction
 * 11. Authorization for Electronic Payments
 * 12. Complete
 *
 * Note: Agreement of Sale (Sky Step 10) is NOT included as it's product-specific
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.RELATIONSHIP_OPTIONS = exports.getPreviousStep = exports.getNextStep = exports.isValidStep = exports.getLastEditedStep = exports.getStepLabel = exports.TOTAL_STEPS = exports.STEPS = exports.STEP_COMPLETE = exports.STEP_AUTHORIZATION_ELECTRONIC = exports.STEP_AUTHORIZATION_SALARY = exports.STEP_DOCUMENTS = exports.STEP_FINANCIAL_DETAILS = exports.STEP_SALARY_BANKING = exports.STEP_HR_DETAILS = exports.STEP_EMPLOYMENT = exports.STEP_FAMILY_CONTACTS = exports.STEP_PERSONAL_DETAILS = exports.STEP_KYC = exports.STEP_START = void 0;
exports.STEP_START = 0;
exports.STEP_KYC = 1;
exports.STEP_PERSONAL_DETAILS = 2;
exports.STEP_FAMILY_CONTACTS = 3;
exports.STEP_EMPLOYMENT = 4;
exports.STEP_HR_DETAILS = 5;
exports.STEP_SALARY_BANKING = 6;
exports.STEP_FINANCIAL_DETAILS = 7;
exports.STEP_DOCUMENTS = 8;
exports.STEP_AUTHORIZATION_SALARY = 9;
exports.STEP_AUTHORIZATION_ELECTRONIC = 10;
exports.STEP_COMPLETE = 11;
exports.STEPS = [
    { id: exports.STEP_START, label: "Loan\nSetup", active: true },
    { id: exports.STEP_KYC, label: "Identity\nVerification", active: false },
    { id: exports.STEP_PERSONAL_DETAILS, label: "Personal\nDetails", active: false },
    { id: exports.STEP_FAMILY_CONTACTS, label: "Family &\nFriend", active: false },
    { id: exports.STEP_EMPLOYMENT, label: "Employment\nDetails", active: false },
    { id: exports.STEP_HR_DETAILS, label: "HR\nDetails", active: false },
    { id: exports.STEP_SALARY_BANKING, label: "Salary\nBanking", active: false },
    { id: exports.STEP_FINANCIAL_DETAILS, label: "Financial\nDetails", active: false },
    { id: exports.STEP_DOCUMENTS, label: "Required\nDocuments", active: false },
    { id: exports.STEP_AUTHORIZATION_SALARY, label: "Salary\nDeduction", active: false },
    {
        id: exports.STEP_AUTHORIZATION_ELECTRONIC,
        label: "Electronic\nPayments",
        active: false,
    },
    { id: exports.STEP_COMPLETE, label: "Complete", active: false },
];
exports.TOTAL_STEPS = exports.STEPS.length;
// Validation functions for each step
var getStepLabel = function (stepId) {
    var step = exports.STEPS.find(function (s) { return s.id === stepId; });
    return step ? step.label.replace("\n", " ") : "Unknown Step";
};
exports.getStepLabel = getStepLabel;
/**
 * Determines the last edited step from a draft loan application
 * Returns the step where the user should resume editing
 *
 * @param loanData - The loan application data
 * @param kycCompleted - Whether KYC verification was completed
 * @returns Step number to resume from
 */
var getLastEditedStep = function (loanData, kycCompleted) {
    // Start is always complete if there's a draft
    if (!loanData)
        return exports.STEP_START;
    // Check if this is a BRAND NEW draft
    // User selects amount on home page, but MUST select branch in Step 0
    // Branch selection is the key requirement to complete Step 0
    var getBranchId = function (branchRef) {
        if (!branchRef)
            return "";
        if (typeof branchRef === "string")
            return branchRef;
        if (typeof branchRef === "object" && "id" in branchRef)
            return branchRef.id;
        return "";
    };
    var hasBranch = !!getBranchId(loanData.branch);
    var hasTerm = loanData.term && String(loanData.term).trim().length > 0;
    var isNewDraft = !hasBranch || !hasTerm;
    if (isNewDraft) {
        // Brand new draft - start at Step 0 to let user select branch and term
        return exports.STEP_START;
    }
    // Check completion flags in reverse order to find last completed step
    // Then return the next incomplete step
    if (loanData.authoriseBank_complete)
        return exports.STEP_COMPLETE; // Ready to submit
    if (loanData.authoriseSalary_complete)
        return exports.STEP_AUTHORIZATION_ELECTRONIC;
    if (loanData.docs_complete)
        return exports.STEP_AUTHORIZATION_SALARY;
    if (loanData.financial_complete)
        return exports.STEP_DOCUMENTS;
    if (loanData.bankDetails_complete)
        return exports.STEP_FINANCIAL_DETAILS;
    if (loanData.paypoint_complete)
        return exports.STEP_SALARY_BANKING;
    if (loanData.employment_complete)
        return exports.STEP_HR_DETAILS;
    if (loanData.family_complete && loanData.friend_complete)
        return exports.STEP_EMPLOYMENT;
    if (loanData.personal_complete)
        return exports.STEP_FAMILY_CONTACTS;
    if (kycCompleted)
        return exports.STEP_PERSONAL_DETAILS;
    // If KYC not completed, start from KYC step
    return exports.STEP_KYC;
};
exports.getLastEditedStep = getLastEditedStep;
var isValidStep = function (stepId) {
    return stepId >= exports.STEP_START && stepId <= exports.STEP_COMPLETE;
};
exports.isValidStep = isValidStep;
// Step navigation helpers
var getNextStep = function (currentStep) {
    if (currentStep >= exports.STEP_COMPLETE)
        return null;
    return currentStep + 1;
};
exports.getNextStep = getNextStep;
var getPreviousStep = function (currentStep) {
    if (currentStep <= exports.STEP_START)
        return null;
    return currentStep - 1;
};
exports.getPreviousStep = getPreviousStep;
// Relationship options for family contacts (matching Sky)
exports.RELATIONSHIP_OPTIONS = [
    { value: "Father", label: "Father" },
    { value: "Mother", label: "Mother" },
    { value: "Brother", label: "Brother" },
    { value: "Sister", label: "Sister" },
    { value: "Sibling", label: "Sibling" },
    { value: "Spouse", label: "Spouse" },
    { value: "Partner", label: "Partner" },
    { value: "Daughter", label: "Daughter" },
    { value: "Son", label: "Son" },
    { value: "Aunt", label: "Aunt" },
    { value: "Uncle", label: "Uncle" },
    { value: "Niece", label: "Niece" },
    { value: "Nephew", label: "Nephew" },
    { value: "Cousin", label: "Cousin" },
    { value: "Grandmother", label: "Grandmother" },
    { value: "Grandfather", label: "Grandfather" },
    { value: "Granddaughter", label: "Granddaughter" },
    { value: "Grandson", label: "Grandson" },
    { value: "Stepmother", label: "Stepmother" },
    { value: "Stepfather", label: "Stepfather" },
    { value: "Stepdaughter", label: "Stepdaughter" },
    { value: "Stepson", label: "Stepson" },
];
